<template>
  <b-modal
    id="modal-edit"
    modal-class="mc-modal-link"
    :title="$t('setting.list.managementAsset.subdomain.changeDomain')"
    size="md"
    centered
    scrollable
    header-class="mc-modal-header__subdomain"
    no-close-on-backdrop
    no-close-on-esc
    @show="onModalShow"
  >
    <FormulateForm name="modal-edit-form" @submit="handleSave">
      <div>
        <b-form-group
          id="companyName"
          :label="$t('setting.list.managementAsset.subdomain.companyName')"
          label-for="companyName"
          label-class="required"
        >
          <b-form-input
            id="companyName"
            v-model="formValues.companyName"
            :placeholder="$t('setting.list.managementAsset.subdomain.companyName')"
            :class="{
              'is-invalid': isEmptyCompanyName,
            }"
            trim
            :disabled="isLoading"
            @blur="companyNameTouched = true"
          />
          <div class="invalid-feedback">
            <span v-if="companyNameTouched && isEmptyCompanyName">
              {{ $t('setting.list.managementAsset.subdomain.companyName') }}
              {{ $t('global.validation.required') }}
            </span>
          </div>
        </b-form-group>
      </div>

      <FormulateInput
        v-model="formValues.domainName"
        type="text"
        name="domainName"
        :label="$t('setting.list.managementAsset.subdomain.domainName')"
        :placeholder="$t('setting.list.managementAsset.subdomain.domainName')"
        readonly
        disabled
      />

      <div>
        <b-form-group
          id="email"
          :label="$t('global.object.email')"
          label-for="email"
          label-class="required"
        >
          <b-form-input
            id="email"
            v-model="formValues.email"
            :placeholder="$t('global.object.email')"
            :class="{
              'is-invalid': (!isEmailValid && emailTouched) || (emailTouched && isEmptyEmail),
            }"
            trim
            :disabled="isLoading"
            @blur="emailTouched = true"
          />
          <div class="invalid-feedback">
            <span v-if="emailTouched && isEmptyEmail">
              {{ $t('global.object.email') }} {{ $t('global.validation.required') }}
            </span>
            <span v-else-if="emailTouched && !isEmailValid && formValues.email">
              {{ $t('global.validation.invalidFormatEmail') }}
            </span>
          </div>
        </b-form-group>
      </div>

      <div class="custom-checbox">
        <b-form-group>
          <input
            v-model="formValues.isCheckedPassword"
            class="form-check-input me-2 custom-control-input"
            type="checkbox"
            :disabled="isLoading"
          />
          <label>{{ $t('master.user.sendEmailForPassword') }}</label>
        </b-form-group>
      </div>
    </FormulateForm>

    <template #modal-footer="{ cancel }">
      <template v-if="isLoading">
        <b-button variant="outline-secondary" disabled>
          <b-spinner small />
          {{ $t('global.object.inProgress') }}
        </b-button>
      </template>
      <template v-else>
        <McButton variant="secondary" :text="$t('global.cancel')" class="mr-3" @click="cancel()" />
        <McButton variant="primary" :text="$t('global.save')" @click="submitForm" />
      </template>
    </template>
  </b-modal>
</template>

<script>
import McButton from '@/components/McButton'
import { diffObject } from '@/helpers/resourceful/compare'
import { Helper } from '@/mixin/helper'
import {
  GET_DETAIL_SUB_DOMAIN,
  UPDATE_SUB_DOMAIN,
} from '@/services/api/setting/personalization/domainManagement'
import * as invitationApi from '@/services/rest-api-identity/lib/invitation/invitation'

export default {
  components: {
    McButton,
  },
  mixins: [Helper],
  props: {
    companyId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      formValues: {
        companyName: null,
        domainName: null,
        email: null,
        isCheckedPassword: false,
      },
      userId: null,
      oldCompanyData: null,
      hasChanged: false,
      emailTouched: false,
      isEmptyEmail: false,
      isLoading: false,
      companyNameTouched: false,
      isEmptyCompanyName: false,
      isSuccessfulUpdate: false,
    }
  },

  computed: {
    isEmailValid() {
      return this.validationEmail(this.formValues.email)
    },
  },

  methods: {
    async onModalShow() {
      this.$nextTick().then(async () => {
        if (this.companyId) {
          this.isLoading = true
          this.formValues.isCheckedPassword = false
          this.formValues.companyName = null
          this.formValues.domainName = null
          this.formValues.email = null
          await this.fetchSubDomainDetails()
          this.isLoading = false
        }
      })
    },

    async fetchSubDomainDetails() {
      const filter = {
        companyId: this.companyId,
      }

      const res = await this.$apollo.query({
        query: GET_DETAIL_SUB_DOMAIN,
        fetchPolicy: 'no-cache',
        variables: {
          filter: filter,
        },
      })

      const result = res.data.detailDomainManagement
      if (!result.isSuccessful) {
        this.$mcToast.error(result.message)
        this.$bvModal.hide('modal-edit')
        return
      }
      const data = result.domainManagement
      this.formValues.companyName = data.companyName
      this.formValues.domainName = data.domainName
      this.formValues.email = data.email
      this.userId = data.userId
      this.oldCompanyData = { ...this.formValues }
    },

    async submitForm() {
      if (this.formValues.email === '') {
        this.emailTouched = true
        this.isEmptyEmail = true
        return
      }
      if (!this.isEmailValid) {
        this.emailTouched = true
        this.isEmptyEmail = false
        return
      }

      if (this.formValues.companyName === '') {
        this.isEmptyCompanyName = true
        this.companyNameTouched = true
        return
      }

      this.emailTouched = false
      this.isEmptyEmail = false
      this.companyNameTouched = false
      this.isEmptyCompanyName = false
      this.$formulate.submit('modal-edit-form')
    },

    async handleSave() {
      this.checkCompanyChanges()
      if (this.hasChanged) {
        const update = await this.$apollo.query({
          query: UPDATE_SUB_DOMAIN,
          fetchPolicy: 'no-cache',
          variables: {
            input: {
              userId: this.userId,
              companyId: this.companyId,
              oldData: {
                email: this.oldCompanyData.email,
                companyName: this.oldCompanyData.companyName,
              },
              newData: {
                email: this.formValues.email,
                companyName: this.formValues.companyName,
              },
            },
          },
        })

        const result = update.data.updateDomainManagement
        if (!result.isSuccessful) {
          let errorMessage = ''
          if (result.message.includes('Company name already exists')) {
            errorMessage = `${this.formValues.companyName} ${this.$t(
              'global.alert.text.alreadyExist'
            )}`
          } else if (result.message.includes('Email already exists')) {
            errorMessage = `${this.formValues.email} ${this.$t('global.alert.text.alreadyExist')}`
          } else {
            errorMessage = result.message
          }
          this.$mcToast.error(errorMessage)
          return
        }

        this.isSuccessfulUpdate = true
      }

      if (this.formValues.isCheckedPassword && this.hasChanged) {
        const INTERNAL_USER_API =
          process.env.VUE_APP_ENV === 'production'
            ? process.env.VUE_APP_INTERNAL_USER_PROD_API
            : process.env.VUE_APP_INTERNAL_USER_API
        const res = await invitationApi.sendInvitation({
          resource_url: `${INTERNAL_USER_API}${this.userId}`,
        })
        if (!res.status) {
          this.$mcToast.error(this.$t('global.alert.text.failSendEmailPassword'))
          return
        }
        this.isSuccessfulUpdate = true
      }

      this.closeAndReset()
    },

    closeAndReset() {
      this.$bvModal.hide('modal-edit')
      this.$emit('update-data')
      this.hasChanged = false
      if (this.isSuccessfulUpdate) {
        this.$mcToast.success(this.$t('global.alert.text.successChangeDomain'))
      }
    },

    checkCompanyChanges() {
      if (this.oldCompanyData) {
        const [_, hasChanged] = diffObject(this.formValues, this.oldCompanyData)
        if (hasChanged) {
          this.hasChanged = true
        }
      }
    },
  },
}
</script>
